import React, { useCallback, useMemo } from 'react'
import { Row, Col, Collapse, Popconfirm, Popover } from 'antd'
import { CopyOutlined, PlusOutlined, MinusOutlined, FileAddOutlined, EyeOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { renderTranslation, stopPropagation } from '../../Helpers/GenericHelpers'
import { cleanVirtualSubrule, toggleSubRuleSection } from '../../Slices/Omnichannel/SeasonRuleSlice'
import { deleteAllSeasonRules } from '../../Services/Omnichannel/SeasonRuleService'
import { DateFormat, RuleType, TimeFormat } from '../../Constants/Configuration'
import {
  createNewVirtualRule,
  isRuleEditToDisable,
  isRuleLockedByOtherUsers,
  selectedRuleIsInSeason,
} from '../../Helpers/DiscountHelper'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useNavigateWrapper } from '../../Helpers/NavigateHelper'
import { customEqual as lockEquality } from 'Helpers/LockHelper'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { TypeOfLock } from 'Services/Omnichannel/LockService'

const { Panel } = Collapse

function SeasonRuleTable(props) {
  const dispatch = useAppDispatch()
  const navigationWrapper = useNavigateWrapper()
  const language = useAppSelector(state => state.present.language.value)
  const ruleId = useAppSelector(state => state.present.seasonRule.ruleId)
  const subRuleSectionExpand = useAppSelector(state => state.present.seasonRule.subRuleSectionExpand)
  const subRuleId = useAppSelector(state => state.present.subRule.subRuleId)
  const user = useAppSelector(state => state.present.configuration.userValue)
  const locks = useAppSelector(state => state.present.locks.locks, lockEquality)

  const currentSeason = props.seasonObj?.season
  const seasonId = currentSeason?.id
  const isCarryOver = currentSeason?.carryOver
  const isOutlet = currentSeason?.outlet
  const isAlwaysOnSaleSeason = currentSeason?.alwaysOnSale
  const seasonRules = props.seasonObj?.rules
  const isSeasonVirtual = props.seasonObj?.isVirtual
  const isSelectedRuleInSeason = selectedRuleIsInSeason(seasonRules, ruleId, subRuleId)

  const addDiscountRule = useCallback(
    e => {
      dispatch(createNewVirtualRule(seasonRules, props.seasonrulelist, language, currentSeason, navigationWrapper))
    },
    [dispatch, seasonRules, props.seasonrulelist, language, currentSeason],
  )

  const deleteSeasonRules = useCallback(
    e => {
      stopPropagation(e)
      dispatch(deleteAllSeasonRules(props.seasonObj, isSelectedRuleInSeason, navigationWrapper))
    },
    [dispatch, isSelectedRuleInSeason, props.seasonObj],
  )

  const expandSubRuleSection = useCallback(
    ruleId => {
      dispatch(toggleSubRuleSection(ruleId))
    },
    [dispatch],
  )

  const copySeasonRule = useCallback(
    e => {
      stopPropagation(e)

      props.copyRule(props.seasonObj)
    },
    [props],
  )

  const sortedseasonRules = useMemo(() => {
    const SeasonRulesList = seasonRules && seasonRules.length > 0 ? [...seasonRules] : []
    return SeasonRulesList.sort((a, b) => b.isActive - a.isActive || Date.parse(a.startDate) - Date.parse(b.startDate))
  }, [seasonRules])

  const isLocked = useCallback(
    ruleId => isRuleLockedByOtherUsers(locks, ruleId, TypeOfLock.Omnichannel, user),
    [locks, user],
  )

  const cleanProps = { ...props }
  delete cleanProps.seasonObj
  delete cleanProps.copyRule

  return (
    <Panel
      {...cleanProps}
      className={isSeasonVirtual ? 'collapse-panel-bordered' : ''}
      collapsible='header'
      header={
        <Row className='label-collapse-header season-rule-table-header' justify='space-between' align='middle'>
          <Col className='select-box label-collapse'>
            {isCarryOver || isOutlet || isAlwaysOnSaleSeason
              ? isCarryOver
                ? renderTranslation(language, TranslationKeys.seasonBoxCarryOver)
                : isOutlet
                ? renderTranslation(language, TranslationKeys.seasonBoxOutlet)
                : renderTranslation(language, TranslationKeys.seasonBoxAlwaysOnSale)
              : currentSeason.name}
          </Col>
          <Col className='icon-box'>
            {!isCarryOver && !isOutlet && !isAlwaysOnSaleSeason ? (
              !isSeasonVirtual ? (
                <Popover
                  content={
                    seasonRules.length === 0
                      ? renderTranslation(language, TranslationKeys.warningNoRuleToCopy)
                      : renderTranslation(language, TranslationKeys.seasonBoxPopoverCopySeason)
                  }
                  trigger='hover'
                >
                  <CopyOutlined
                    rotate={180}
                    className={'generic-icon' + (seasonRules.length === 0 ? ' disable' : '')}
                    onClick={copySeasonRule}
                  />
                </Popover>
              ) : null
            ) : null}
            {!isCarryOver && !isOutlet && !isAlwaysOnSaleSeason ? (
              seasonRules.length !== 0 ? (
                <Popover
                  content={renderTranslation(language, TranslationKeys.seasonBoxPopoverHideSeason)}
                  trigger='hover'
                >
                  <Popconfirm
                    title={renderTranslation(language, TranslationKeys.deleteMultiRulePopoverText)}
                    onConfirm={deleteSeasonRules}
                    onCancel={stopPropagation}
                    okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                    cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                    icon={null}
                  >
                    <EyeOutlined onClick={stopPropagation} className='generic-icon' />
                  </Popconfirm>
                </Popover>
              ) : (
                <EyeOutlined className='generic-icon' onClick={deleteSeasonRules} />
              )
            ) : null}
          </Col>
        </Row>
      }
      key={'panel-' + seasonId}
    >
      <table className='season-rule-table' id={'season-rule-table-' + props.index}>
        <tbody>
          {(isCarryOver || isOutlet || isAlwaysOnSaleSeason) && seasonRules.length === 0 ? (
            <tr className='cursor-pointer'>
              <td colSpan={2} className='add-season-cell' onClick={addDiscountRule}>
                <div className='d-flex-c'>
                  <FileAddOutlined className='generic-icon add-season-rule-icon' />
                  <div className='pl-1'>{renderTranslation(language, TranslationKeys.seasonBoxAddSeasonRule)}</div>
                </div>
              </td>
            </tr>
          ) : null}
          {sortedseasonRules.map(rule => {
            const isCurrentRowSelected = ruleId === (rule.virtualId ? rule.virtualId : rule.id)
            const subRules: any[] = rule.subrules ?? []
            const isSubRuleSectionOpen = subRuleSectionExpand.indexOf(rule.id) !== -1
            const locked = isLocked(rule.id)
            const isEditDisable = isRuleEditToDisable(rule, rule.isActive) || locked
            const startDateTime = dayjs(rule.startDate)
            const endDateTime = dayjs(rule.endDate)
            const startDate = startDateTime.format(DateFormat)
            const startTime = startDateTime.format(TimeFormat)
            const endDate = endDateTime.format(DateFormat)
            const endTime = endDateTime.format(TimeFormat)
            const subRuleLocked = isLocked(subRules[0]?.id)
            return rule ? (
              <React.Fragment key={'rule-' + rule.id + (rule.virtualId ? '-' + rule.virtualId : '')}>
                <tr
                  className={
                    'cursor-pointer' +
                    (!rule.isActive ? ' disable' : '') +
                    (rule.ruleType === RuleType.salesPeriod ? ' sales-active' : '') +
                    (rule.ruleType === RuleType.promo ? ' promo-active' : '') +
                    (isCurrentRowSelected && !subRuleId ? ' selected' : '') +
                    (!isEditDisable || rule.virtualId ? ' future-rule-edit' : !rule.isActive ? ' ' : ' active-rule')
                  }
                  onClick={() => {
                    navigationWrapper.navigateToRule(rule.virtualId ? rule.virtualId : rule.id)
                    dispatch(cleanVirtualSubrule(undefined))
                  }}
                >
                  <td className={'rule-name-cell'}>
                    <div
                      className={
                        'pretext-block' +
                        (isCurrentRowSelected && !subRuleId ? ' selected' : '') +
                        (isSubRuleSectionOpen ? ' open' : '') +
                        (locked ? ' locked' : '')
                      }
                      onClick={e => {
                        stopPropagation(e)
                        if (subRules.length === 0) {
                          return
                        }
                        expandSubRuleSection(rule.id)
                      }}
                    >
                      {subRules.length > 0 ? (
                        isSubRuleSectionOpen ? (
                          <MinusOutlined className='minus-icon expand-element-icon-alt' />
                        ) : (
                          <PlusOutlined className='plus-icon expand-element-icon-alt' />
                        )
                      ) : null}
                    </div>
                    <div className='rule-title'>{rule.name}</div>
                    <div className='rule-type'>
                      {rule.ruleType === RuleType.salesPeriod
                        ? renderTranslation(language, TranslationKeys.seasonBoxSalesPeriod)
                        : null}
                      {rule.ruleType === RuleType.promo
                        ? renderTranslation(language, TranslationKeys.seasonBoxPromo)
                        : null}
                    </div>
                  </td>
                  <td className='rule-date-cell'>
                    <div className='date-container start-date'>
                      <div className='date'>
                        {rule.ruleType === RuleType.promo
                          ? renderTranslation(language, TranslationKeys.seasonBoxFrom)
                          : null}{' '}
                        {startDate}
                      </div>
                      <div className='time text-xsmall'>({startTime} CEST)</div>
                    </div>
                    {rule.ruleType !== RuleType.season ? (
                      <div className='date-container end-date'>
                        <div className='date'>
                          {renderTranslation(language, TranslationKeys.seasonBoxTo)}
                          {endDate}
                        </div>
                        <div className='d-none'>({endTime})</div>
                      </div>
                    ) : null}
                  </td>
                </tr>
                {subRules.length > 0 && isSubRuleSectionOpen ? (
                  <tr>
                    <td colSpan={2} className='sub-rule-table-container'>
                      <table className='sub-rule-table'>
                        <tbody>
                          {subRules.map(subRule => {
                            const currentId = subRule.virtualId ?? subRule.id
                            const isSubRuleSelected = subRuleId && subRuleId === currentId
                            return (
                              <tr
                                className={'sub-rule-row cursor-pointer' + (isSubRuleSelected ? ' selected' : '')}
                                onClick={() => {
                                  navigationWrapper.navigateToSubrule(currentId)
                                  dispatch(cleanVirtualSubrule(undefined))
                                }}
                                key={'sub-rule-' + subRule.id + (subRule.virtualId ? '-' + currentId : '')}
                              >
                                <td className='sub-rule-cell'>
                                  <div
                                    className={
                                      'pretext-block' +
                                      (isSubRuleSelected ? ' selected' : '') +
                                      (subRuleLocked ? ' locked' : '')
                                    }
                                  />
                                  <div className='sub-rule-title'>{subRule.name}</div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ) : null
          })}
        </tbody>
      </table>
    </Panel>
  )
}

export default SeasonRuleTable
